import {PrismicRichText} from '@prismicio/react'
import type {FourGridSliceCards} from 'prismicio-types'
import {Container} from 'src/common/Container'
import {LinkWrapper} from 'src/common/LinkWrapper'
import {Card} from './Card'
import {ExtendedSection} from './ExtendedSection'

type FourGridCardsProps = {
  slice: FourGridSliceCards & {
    slice_type: string
    variation: string
  }
  pageUid: string
  componentIndex: number
}

export const CardsVariation = ({slice, pageUid, componentIndex}: FourGridCardsProps) => {
  const hasExtendedSection = slice.primary.extended_section && slice.primary.extended_section.length > 0

  return (
    <Container
      backgroundColor={slice.primary.background_color}
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="justify-center !py-10 px-5 md:!py-20 md:px-10 lg:px-5 xl:px-0"
    >
      <div className="flex w-full max-w-[1200px] flex-col gap-10 lg:gap-12" data-aos="fade-up">
        <div className="flex flex-col gap-4 lg:gap-6">
          <span className="text-button-s text-ui-blue-450">{slice.primary.tag}</span>
          <PrismicRichText
            field={slice.primary.title}
            components={{
              heading2: ({children}) => (
                <h3 className="lg:text-tg-h2 text-32 leading-[normal] tracking-[-.32px] text-moss-black">{children}</h3>
              ),
              strong: ({children}) => <span className="text-moss-grey-400">{children}</span>,
            }}
          />
        </div>

        {slice.primary.row_by_row_specification === '4x5' ? (
          <div className="flex flex-col gap-6">
            <div className="grid grid-cols-1 gap-6 min-[500px]:grid-cols-2 lg:grid-cols-4">
              {slice.primary.cards.slice(0, 4).map((card, index) => (
                <LinkWrapper
                  key={`card-link-${index}`}
                  link={card.link}
                  mktgId="four-grid-card"
                  location={`component-${componentIndex}-card-${index}`}
                  pageName={pageUid}
                  dataType={`card-${index}`}
                  className="group/card"
                >
                  <Card title={card.title || ''} subtitle={card.subtitle || ''} icon={card.icon} image={card.image} />
                </LinkWrapper>
              ))}
            </div>
            <div className="grid grid-cols-1 gap-6 min-[500px]:grid-cols-2 lg:grid-cols-5">
              {slice.primary.cards.slice(4, 9).map((card, index) => (
                <LinkWrapper
                  key={`card-link-${index + 4}`}
                  link={card.link}
                  mktgId="four-grid-card"
                  location={`component-${componentIndex}-card-${index + 4}`}
                  pageName={pageUid}
                  dataType={`card-${index + 4}`}
                  className="group/card"
                >
                  <Card
                    title={card.title || ''}
                    subtitle={card.subtitle || ''}
                    icon={card.icon}
                    image={card.image}
                    iconOverlay
                  />
                </LinkWrapper>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 min-[500px]:grid-cols-2 md:flex-row md:flex-wrap lg:grid-cols-4 lg:flex-nowrap">
            {slice.primary.cards.map((card, index) => (
              <LinkWrapper
                key={`card-link-${index}`}
                link={card.link}
                mktgId="four-grid-card"
                location={`component-${componentIndex}-card-${index}`}
                pageName={pageUid}
                dataType={`card-${index}`}
                className="group/card"
              >
                <Card title={card.title || ''} subtitle={card.subtitle || ''} icon={card.icon} image={card.image} />
              </LinkWrapper>
            ))}
          </div>
        )}

        {hasExtendedSection && (
          <ExtendedSection
            title={slice.primary.extended_section_title}
            cards={slice.primary.extended_section}
            pageUid={pageUid}
            componentIndex={componentIndex}
          />
        )}
      </div>
    </Container>
  )
}
